// api/series-rebaseline.ts
//
// Series rebaselines (backend app/services/series_rebaseline.py): one planned,
// dated change to a client's measurement series — lane pin changes, lanes added
// or removed, question edits, rubric/belief changes, and a note saying why —
// applied once, taken by the next read (the new baseline), immutable after.
//
// Every surface that shows a run carries its `series` annotation, and every
// surface that shows a series carries its applied rebaselines. A series never
// rebaselined has none, and every run reads segment 0 / battery 1, which
// renders exactly as before.

import { apiFetch } from "./api-fetch";

/** Per-run series annotation. */
export interface RunSeries {
  /** How many rebaselines sit before this run. The chart breaks the line
   *  wherever this changes between two consecutive points. */
  segment: number;
  battery_version: number;
  /** The rebaseline that opened this run's segment (null on segment 0). */
  rebaseline_id: number | null;
  /** This run is that rebaseline's new baseline (first read after it). */
  new_baseline: boolean;
  /** The question battery this run asked (#137). */
  battery_hash?: string;
  /** Its battery differs from the previous plotted read's with no rebaseline
   *  between them: the two are not comparable (#137). */
  battery_changed?: boolean;
  /** Rises at every rebaseline AND every uncovered battery change; the chart
   *  breaks its lines wherever it changes. Absent from older payloads. */
  line_segment?: number;
}

export interface RebaselinePinChange {
  lane: string;
  from: string | null;
  to: string | null;
}

export interface RebaselineQuestionChange {
  op: "edit" | "add" | "remove";
  query_id: string;
  old_text: string | null;
  new_text: string | null;
  fields?: string[];
}

export interface RebaselineVariableChange {
  op: "edit" | "add" | "remove";
  variable_id: string;
  fields: string[];
  old: Record<string, string> | null;
  new: Record<string, string> | null;
}

export interface RebaselineChanges {
  lanes_before: string[];
  lanes_after: string[];
  lanes_added: string[];
  lanes_removed: string[];
  lane_pins: RebaselinePinChange[];
  questions: RebaselineQuestionChange[];
  variables: RebaselineVariableChange[];
  rubric_version_before: string | null;
  rubric_version_after: string | null;
  // Present on preview payloads (the full plan):
  pin_set_before?: Record<string, string>;
  pin_set_after?: Record<string, string>;
  battery_version_before?: number;
  battery_version_after?: number;
  framework_hash_before?: string;
  framework_hash_after?: string;
  query_count_before?: number;
  query_count_after?: number;
}

/** The record a client sees: date, what changed, the note. */
export interface SeriesRebaseline {
  id: number;
  status: "draft" | "applied" | "cancelled";
  applied_at: string | null;
  note: string;
  battery_version: number | null;
  battery_version_before?: number | null;
  last_read_before_id: number | null;
  first_run_id: number | null;
  boundary_run_id: number | null;
  changes: RebaselineChanges;
  /** Human-readable change list, one line per change, server-rendered. */
  lines: string[];
  // Admin-only fields:
  applied_by?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  cancelled_at?: string | null;
  first_run_bound_at?: string | null;
  spec?: RebaselineSpec;
  pin_set_before?: Record<string, string>;
  pin_set_after?: Record<string, string>;
}

// ── The draft an admin edits ───────────────────────────────────────────────

export type QuestionChangeSpec =
  | { op: "edit"; query_id: string; text?: string; category?: string; persona?: string; variable_ids?: string[] }
  | { op: "add"; query: { id?: string; text: string; category?: string; persona?: string; variable_ids?: string[] } }
  | { op: "remove"; query_id: string };

export type VariableChangeSpec =
  | { op: "edit"; variable_id: string; fields: Partial<Record<"claim" | "rationale" | "score_2" | "score_1" | "score_0", string>> }
  | { op: "add"; variable: { id: string; claim: string; rationale?: string; score_2?: string; score_1?: string; score_0?: string } }
  | { op: "remove"; variable_id: string };

export interface RebaselineSpec {
  /** Absent = keep the series' current lanes. */
  lanes?: string[];
  lane_pins?: Record<string, string>;
  question_changes?: QuestionChangeSpec[];
  variable_changes?: VariableChangeSpec[];
}

export interface SeriesLaneOption {
  id: string;
  label: string;
  unversioned: boolean;
  /** Allowed versions / presets on the lane's default endpoint. */
  choices: string[];
}

/** What the series is read on now — the starting point of a draft. */
export interface CurrentSeries {
  lanes: string[];
  pins: Record<string, string>;
  last_read_id: number | null;
  battery_version: number;
  framework_approved: boolean;
  queries: { id: string; text: string; category?: string; persona?: string; variable_ids?: string[] }[];
  reasoning_variables: { id: string; claim: string; rationale?: string; score_2?: string; score_1?: string; score_0?: string }[];
  available_lanes: SeriesLaneOption[];
  read_in_progress: string | null;
}

export interface RebaselinePreview {
  rebaseline: SeriesRebaseline;
  changes: RebaselineChanges;
  lines: string[];
  /** Reasons the apply would be refused. Apply only when empty. */
  blockers: string[];
  warnings: string[];
  /** Carried back by apply; null when the rebaseline is not a draft. */
  digest: string | null;
  last_read_before_id?: number | null;
}

// ── Calls (admin) ──────────────────────────────────────────────────────────

const base = (customerId: number) => `/onboarding/customers/${customerId}/rebaselines`;

export function listRebaselines(customerId: number): Promise<{
  rebaselines: SeriesRebaseline[];
  current: CurrentSeries;
}> {
  return apiFetch(base(customerId));
}

export function createRebaseline(
  customerId: number,
  body: { spec: RebaselineSpec; note: string },
): Promise<{ rebaseline: SeriesRebaseline }> {
  return apiFetch(base(customerId), { method: "POST", body: JSON.stringify(body) });
}

export function updateRebaseline(
  customerId: number,
  rebaselineId: number,
  body: { spec: RebaselineSpec; note: string },
): Promise<{ rebaseline: SeriesRebaseline }> {
  return apiFetch(`${base(customerId)}/${rebaselineId}`, { method: "PUT", body: JSON.stringify(body) });
}

export function cancelRebaseline(
  customerId: number,
  rebaselineId: number,
): Promise<{ rebaseline: SeriesRebaseline }> {
  return apiFetch(`${base(customerId)}/${rebaselineId}/cancel`, { method: "POST" });
}

export function previewRebaseline(customerId: number, rebaselineId: number): Promise<RebaselinePreview> {
  return apiFetch(`${base(customerId)}/${rebaselineId}/preview`);
}

/** Apply a draft. `digest` must be the preview the admin confirmed. */
export function applyRebaseline(
  customerId: number,
  rebaselineId: number,
  digest: string,
): Promise<{ rebaseline: SeriesRebaseline }> {
  return apiFetch(`${base(customerId)}/${rebaselineId}/apply`, {
    method: "POST",
    body: JSON.stringify({ digest }),
  });
}

// ── Display helpers ────────────────────────────────────────────────────────

export function formatRebaselineDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
